import React from 'react';
import { useNavigate } from 'react-router-dom';
import { FaCheck, FaRocket, FaCrown, FaStar, FaLock } from 'react-icons/fa';
import { stripeEnabled, getStripePlanId } from '../constants';
import axios from 'axios';
import { serverURL } from '../constants';
import { useTranslation } from 'react-i18next';
import logger from '../utils/logger';

const PricingPlan = (props) => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const currentPlan = sessionStorage.getItem('type');
    const isCurrentPlan = currentPlan === (props.data.planId || 'free');
    const isLoggedIn = sessionStorage.getItem('auth') !== null;
    
    async function redirectPayment() {
        // If user is not logged in, redirect to payment
        if (sessionStorage.getItem('auth') === null) {
            navigate('/payment', { state: { plan: props.data.type } });
            return;
        }

        // If user is on free plan and clicks free plan, do nothing
        if (currentPlan === 'free' && (props.data.planId || 'free') === 'free') {
            return;
        }

        // For any plan change, redirect directly to Stripe if enabled
        if (stripeEnabled && (props.data.planId || 'free') !== 'free') {
            try {
                const stripePlanId = props.data.stripePlanId || getStripePlanId(props.data.type);
                if (!stripePlanId) {
                    logger.error('No Stripe plan ID found for:', props.data.type);
                    navigate('/payment', { state: { plan: props.data.type } });
                    return;
                }

                const dataToSend = {
                    planId: stripePlanId,
                    email: sessionStorage.getItem('email')
                };

                const response = await axios.post(`${serverURL}/api/stripepayment`, dataToSend);
                
                if (response.data.status === 'success' && response.data.url) {
                    sessionStorage.setItem('stripe', response.data.id);
                    sessionStorage.setItem('method', 'stripe');
                    sessionStorage.setItem('plan', props.data.type);
                    window.location.replace(response.data.url);
                    return;
                }
            } catch (error) {
                logger.error('Stripe payment error:', error);
                navigate('/payment', { state: { plan: props.data.type } });
                return;
            }
        }

        // If Stripe is not enabled or plan ID not found, fallback to payment page
        if (currentPlan !== (props.data.planId || 'free')) {
            navigate('/payment', { state: { plan: props.data.type } });
            return;
        }

        // If user is on the same paid plan, redirect to subscription management
        navigate('/subscription');
    }

    // Prefer features from Super Admin / API; fall back to i18n defaults
    const getFeatures = () => {
        const fromApi = Array.isArray(props.data.features) ? props.data.features : null;
        if (fromApi && fromApi.length) {
            return fromApi.map((f) => {
                if (typeof f === 'string') return { text: f, included: true };
                return {
                    text: String(f?.text || f?.name || '').trim(),
                    included: f?.included !== false,
                };
            }).filter((f) => f.text);
        }

        const planId = props.data.planId || 'free';
        switch(planId) {
            case 'free':
                return [
                    { text: t('pricing.freePlan.features.textCourses'), included: true },
                    { text: t('pricing.freePlan.features.videoCourses'), included: false },
                    { text: t('pricing.freePlan.features.aiTokens'), included: true },
                    { text: t('pricing.freePlan.features.subTopics'), included: true },
                    { text: t('pricing.freePlan.features.basicCreation'), included: true },
                    { text: t('pricing.freePlan.features.standardSupport'), included: true },
                    { text: t('pricing.monthlyPlan.features.aiStoryteller'), included: false },
                    { text: t('pricing.monthlyPlan.features.betterResults'), included: false },
                    { text: t('pricing.monthlyPlan.features.createAiStoryteller'), included: false }
                ];
            case 'monthly':
                return [
                    { text: t('pricing.monthlyPlan.features.unlimitedText'), included: true },
                    { text: t('pricing.monthlyPlan.features.unlimitedVideo'), included: true },
                    { text: t('pricing.monthlyPlan.features.subTopics'), included: true },
                    { text: t('pricing.monthlyPlan.features.aiStoryteller'), included: true },
                    { text: t('pricing.monthlyPlan.features.prioritySupport'), included: true },
                    { text: t('pricing.monthlyPlan.features.betterResults'), included: true },
                    { text: t('pricing.monthlyPlan.features.createAiStoryteller'), included: true }
                ];
            case 'yearly':
                return [
                    { text: t('pricing.yearlyPlan.features.unlimitedText'), included: true },
                    { text: t('pricing.yearlyPlan.features.unlimitedVideo'), included: true },
                    { text: t('pricing.yearlyPlan.features.subTopics'), included: true },
                    { text: t('pricing.yearlyPlan.features.aiStoryteller'), included: true },
                    { text: t('pricing.yearlyPlan.features.prioritySupport'), included: true },
                    { text: t('pricing.yearlyPlan.features.betterResults'), included: true },
                    { text: t('pricing.yearlyPlan.features.createAiStoryteller'), included: true },
                    { text: t('pricing.yearlyPlan.freeMonths'), included: true }
                ];
            default:
                return [];
        }
    };

    const getPlanIcon = () => {
        const planId = props.data.planId || 'free';
        switch(planId) {
            case 'free':
                return <FaRocket className="w-6 h-6" />;
            case 'monthly':
                return <FaStar className="w-6 h-6" />;
            case 'yearly':
                return <FaCrown className="w-6 h-6" />;
            default:
                return <FaRocket className="w-6 h-6" />;
        }
    };

    const getButtonText = () => {
        // If this is the current plan, show "Current Plan"
        if (isCurrentPlan) {
            return (
                <div className="flex items-center justify-center gap-2">
                    <FaCheck className="w-4 h-4" />
                    <span>{t('pricing.currentPlan.button')}</span>
                </div>
            );
        }

        // If user is not logged in, show "Get Started"
        if (!isLoggedIn) {
            return t('pricing.freePlan.buttonText');
        }

        // If user is on free plan and viewing a paid plan, show "Upgrade"
        if (currentPlan === 'free' && (props.data.planId || 'free') !== 'free') {
            return t('pricing.upgrade');
        }

        // For all other cases (modifying existing paid plan), show "Modify Plan"
        return t('pricing.modifyPlan');
    };

    return (
        <div className={`relative flex flex-col w-full max-w-sm bg-white/90 dark:bg-gray-900/90 backdrop-blur-md rounded-2xl shadow-xl border ${
            isCurrentPlan 
                ? 'border-blue-500 dark:border-blue-400 ring-2 ring-blue-500/20 dark:ring-blue-400/20' 
                : 'border-gray-200 dark:border-gray-700'
        } overflow-hidden group hover:shadow-2xl transition-all duration-300`}>
            {/* Gradient border effect */}
            <div className="absolute inset-0 bg-gradient-to-br from-blue-500/20 via-pink-500/20 to-purple-500/20 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
            
            {/* Current Plan Badge */}
            {isCurrentPlan && (
                <div className="absolute top-4 left-4 bg-blue-500 text-white px-3 py-1 rounded-full text-sm font-medium shadow-lg z-10">
                    {t('pricing.currentPlan.badge')}
                </div>
            )}
            
            {/* Plan Icon */}
            <div className="absolute top-0 end-0 p-4">
                <div className={`w-12 h-12 rounded-full flex items-center justify-center text-white shadow-lg ${
                    (props.data.planId || 'free') === 'free'
                        ? 'bg-gradient-to-br from-gray-500 to-gray-600' 
                        : 'bg-gradient-to-br from-blue-500 to-purple-500'
                }`}>
                    {getPlanIcon()}
                </div>
            </div>

            <div className="relative p-8">
                {/* Plan Type */}
                <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-2 pe-16">
                    {props.data.type}
                </h3>
                
                {/* Price */}
                <div className="flex items-baseline mb-6">
                    <span className={`text-4xl font-bold ${
                        (props.data.planId || 'free') === 'free'
                            ? 'text-gray-900 dark:text-white' 
                            : 'bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent'
                    }`}>{props.data.currency === 'EUR' || !props.data.currency ? '€' : `${props.data.currency} `}{props.data.cost}</span>
                    <span className="ms-2 text-gray-600 dark:text-gray-400">
                        {props.data.period
                            ? `/${props.data.period}`
                            : `/${t(`pricing.${(props.data.planId || 'free') === 'free' ? 'freePlan' : (props.data.planId || 'free') === 'monthly' ? 'monthlyPlan' : 'yearlyPlan'}.period`)}`}
                    </span>
                    {props.data.save && (
                        <div className="ms-2">
                            <span className="text-sm text-indigo-600 dark:text-indigo-400 font-medium">
                                {props.data.save}
                            </span>
                        </div>
                    )}
                    {!props.data.save && (props.data.planId || 'free') === 'yearly' && (
                        <div className="ms-2">
                            <span className="text-sm text-indigo-600 dark:text-indigo-400 font-medium">
                                {t('pricing.yearlyPlan.save')}
                            </span>
                            <div className="text-xs text-gray-500 dark:text-gray-400">
                                {t('pricing.yearlyPlan.freeMonths')}
                            </div>
                        </div>
                    )}
                </div>

                {/* Features List */}
                <ul className="space-y-4 mb-8">
                    {getFeatures().map((feature, index) => (
                        <li key={index} className="flex items-start group/item">
                            <div className={`flex-shrink-0 w-5 h-5 rounded-full flex items-center justify-center me-3 mt-0.5 ${
                                feature.included 
                                    ? 'bg-indigo-100 dark:bg-indigo-900/50 group-hover/item:bg-indigo-200 dark:group-hover/item:bg-indigo-800/50' 
                                    : 'bg-gray-100 dark:bg-gray-800/50'
                            }`}>
                                {feature.included ? (
                                    <FaCheck className="w-3 h-3 text-indigo-600 dark:text-indigo-400" />
                                ) : (
                                    <FaLock className="w-3 h-3 text-gray-400 dark:text-gray-500" />
                                )}
                            </div>
                            <span className={`text-base ${
                                feature.included 
                                    ? 'text-gray-900 dark:text-white group-hover/item:text-blue-600 dark:group-hover/item:text-blue-400' 
                                    : 'text-gray-500 dark:text-gray-400'
                            }`}>
                                {feature.text}
                            </span>
                        </li>
                    ))}
                </ul>

                {/* CTA Button */}
                <button
                    onClick={redirectPayment}
                    className={`w-full py-3.5 px-6 rounded-xl ${
                        isCurrentPlan 
                            ? 'bg-gradient-to-r from-indigo-500 to-indigo-600 hover:from-indigo-600 hover:to-indigo-700' 
                            : (props.data.planId || 'free') === 'free'
                                ? 'bg-gray-600 hover:bg-gray-700'
                                : 'bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700'
                    } text-white font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 dark:focus:ring-offset-gray-900 relative overflow-hidden group/btn`}
                >
                    <span className="relative z-10">
                        {getButtonText()}
                    </span>
                    <div className="absolute inset-0 bg-gradient-to-r from-purple-600 to-blue-600 opacity-0 group-hover/btn:opacity-100 transition-opacity duration-200" />
                </button>
            </div>
        </div>
    );
};

export default PricingPlan;
